import React, { useState } from 'react';
import { Box, Button, Divider, Paper, TextField, Typography } from '@mui/material';
import { Loader } from '../../../core/components';
import bankIdIcon from '../../../assets/images/BankId-DNA-element-white.svg';
import loginWindowBg from '../../../assets/images/Login-window.svg';
import LanguageSelector from '../LanguageSelector/LanguageSelector';
import { LanguageCode } from '../../../core/i18n/languages';
import { loginTranslations } from '../loginTranslations';

type LoginPageProps = {
  language: LanguageCode;
  onLanguageChange: (code: LanguageCode) => void;
  onLoginClick: () => void;
  isLoading?: boolean;
  error?: string | null;
  logoUrl?: string;
  caseStatusEnabled?: boolean;
  onCaseSearch?: (caseNumber: string) => void;
};

const LoginPage = ({
  language,
  onLanguageChange,
  onLoginClick,
  isLoading = false,
  error = null,
  logoUrl,
  caseStatusEnabled = false,
  onCaseSearch,
}: LoginPageProps) => {
  const [caseNumber, setCaseNumber] = useState('');
  const t = loginTranslations[language];

  const handleLoginClick = () => {
    if (!isLoading) {
      onLoginClick();
    }
  };

  const handleCaseSearch = () => {
    const trimmed = caseNumber.trim();
    if (trimmed) onCaseSearch?.(trimmed);
  };

  const defaultLogoUrl = require('../../../assets/images/logo.svg').default;

  const primaryBtnSx = {
    py: 1.5,
    borderRadius: '25px',
    textTransform: 'none',
    fontSize: 16,
    fontWeight: 500,
  } as const;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        backgroundColor: 'var(--brand-color-3)',
        backgroundImage: `url(${loginWindowBg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <Box
        component="header"
        sx={{
          height: 60,
          px: 2.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: 'var(--header-background-color)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box component="img" src={logoUrl || defaultLogoUrl} alt="My Page" sx={{ height: 40, width: 'auto' }} />
        </Box>
        <LanguageSelector value={language} onChange={onLanguageChange} />
      </Box>

      <Box component="main" sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2.5 }}>
        <Paper elevation={3} sx={{ p: '25px', maxWidth: 340, width: '90vw', borderRadius: 2 }}>
          <Typography component="h1" sx={{ fontWeight: 700, fontSize: 24, textAlign: 'center', color: 'var(--brand-color-1)', mb: 3 }}>
            {t.welcome}
          </Typography>

          {error && (
            <Box
              role="alert"
              sx={{ color: '#821517', bgcolor: '#f8e4e3', border: '1px solid #b12025', p: 2, mb: 2.5, fontSize: 14 }}
            >
              {error}
            </Box>
          )}

          <Button
            fullWidth
            onClick={handleLoginClick}
            disabled={isLoading}
            aria-label={t.loginWithBankId}
            startIcon={!isLoading ? <Box component="img" src={bankIdIcon} alt="" sx={{ width: 30, height: 22 }} /> : undefined}
            sx={{
              ...primaryBtnSx,
              backgroundColor: 'var(--btn-background-color-primary)',
              color: 'var(--btn-text-color-primary)',
              '&:hover': { backgroundColor: 'var(--btn-background-color-primary)', filter: 'brightness(0.92)' },
              '&.Mui-disabled': { backgroundColor: '#ccc', color: '#fff' },
            }}
          >
            {isLoading ? <Loader /> : t.loginWithBankId}
          </Button>

          {caseStatusEnabled && (
            <>
              <Divider sx={{ my: 2.5, color: '#999', fontSize: 12 }}>{t.or}</Divider>

              <Typography
                component="label"
                htmlFor="caseNumber"
                sx={{ display: 'block', fontSize: 13, fontWeight: 600, textTransform: 'uppercase', color: 'var(--brand-color-1)', mb: 0.75 }}
              >
                {t.checkCaseStatus}
              </Typography>
              <TextField
                id="caseNumber"
                fullWidth
                size="small"
                placeholder={t.caseNumberPlaceholder}
                value={caseNumber}
                onChange={(e) => setCaseNumber(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCaseSearch()}
                slotProps={{ htmlInput: { maxLength: 20 } }}
                sx={{ mb: 0.75 }}
              />
              {t.caseNumberHint && (
                <Typography sx={{ fontSize: 12, color: '#999', mb: 2 }}>{t.caseNumberHint}</Typography>
              )}
              <Button
                fullWidth
                variant="outlined"
                onClick={handleCaseSearch}
                disabled={!caseNumber.trim()}
                sx={{
                  ...primaryBtnSx,
                  color: 'var(--btn-background-color-primary)',
                  borderColor: 'var(--btn-background-color-primary)',
                  borderWidth: 1.5,
                  '&:hover': {
                    backgroundColor: 'var(--btn-background-color-primary)',
                    color: 'var(--btn-text-color-primary)',
                    borderColor: 'var(--btn-background-color-primary)',
                  },
                }}
              >
                {t.seeStatusAndAmount}
              </Button>
            </>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default LoginPage;
